// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/calibrationScoring.js
//  MICROSERVICIO ONBOARDING v3 — Lógica de scoring y branching adaptativo
//
//  Fuente normativa: docs/onboarding-master-v2.md, PARTE II §1.
//
//  ── REGLAS TEXTUALES DEL DOCUMENTO ───────────────────────────────────────────
//  1. "Score por nivel: 5 preguntas correctas de un nivel = pasa al siguiente
//      automáticamente en la calibración adaptativa."
//  2. "Corte adaptativo: si falla 3 de 5 en un nivel, el sistema detiene ahí."
//  3. "technical_score final: se calcula como el nivel más alto donde acertó
//      >= 3 de 5, con un decimal de ajuste según el nivel siguiente."
//
//  ── DERIVACIÓN DE LA FÓRMULA (documentado por transparencia) ─────────────────
//  El documento no publica la fórmula cerrada, solo dos ejemplos resueltos:
//
//    Ej. A (JSON Parte II §1): A1 5/5, A2 4/5, B1 2/5  →  technical_score 5.6
//    Ej. B (prosa Parte II §1): B1 4/5, B2 2/5         →  technical_score 6.4
//
//  Ambos comparten el mismo patrón (4/5 en el nivel superado, 2/5 en el
//  siguiente) y difieren en 0.8 puntos por un salto de un nivel. De ahí:
//
//    · Paso entre anclas de nivel = 0.8
//    · Ancla de A2 = 5.0  y  ancla de B1 = 5.8  (únicos valores que reproducen
//      ambos ejemplos con un mismo término de ajuste)
//    · Término de ajuste = aciertos_del_nivel_siguiente × 0.3
//      ("un decimal de ajuste según el nivel siguiente", literal del documento)
//
//  Verificación:
//    Ej. A → 5.0 + (4-4)×0.2 + 2×0.3 = 5.6  ✅ coincide con el documento
//    Ej. B → 5.8 + (4-4)×0.2 + 2×0.3 = 6.4  ✅ coincide con el documento
//
//  ⚠️  DOS CASOS QUE EL DOCUMENTO NO CUBRE, resueltos aquí por extrapolación
//      explícita (no por invención de reglas nuevas de negocio):
//        a) Superar C1 — no existe "nivel siguiente" del cual sacar el ajuste.
//           Se usa el desempeño en el propio C1, calibrado para que un barrido
//           perfecto (5/5 en C1) dé exactamente 10.0, el techo de la escala.
//        b) No superar ni A1 — no existe "nivel más alto superado". Se asigna
//           un piso de 1.0 más el crédito parcial de los aciertos de A1.
//      Si el equipo quiere otros valores para estos dos bordes, se cambian aquí
//      y en el documento maestro — el resto de la fórmula NO se toca.
// ═══════════════════════════════════════════════════════════════════════════════

import { LEVELS, CALIBRATION_BANK } from './calibrationBank';

/** Aciertos mínimos para superar un nivel (regla 2: fallar 3 de 5 = corte). */
export const PASS_THRESHOLD = 3;

/** Fallos que disparan el corte adaptativo dentro de un nivel. */
export const CUTOFF_FAILS = 3;

/** Anclas por nivel — paso 0.8, ajustadas a los dos ejemplos del documento. */
const LEVEL_ANCHOR = {
  A1: 4.2,
  A2: 5.0,
  B1: 5.8,
  B2: 6.6,
  C1: 7.4,
};

const WITHIN_LEVEL_WEIGHT = 0.2;  // matiz por desempeño dentro del nivel superado
const NEXT_LEVEL_WEIGHT = 0.3;    // "decimal de ajuste según el nivel siguiente"

/** Devuelve el nivel siguiente, o null si ya es el tope de la escala. */
export function getNextLevel(level) {
  const i = LEVELS.indexOf(level);
  if (i === -1 || i === LEVELS.length - 1) return null;
  return LEVELS[i + 1];
}

/**
 * Calcula el technical_score y el CEFR estimado a partir de los resultados
 * por nivel de la calibración.
 *
 * @param {Object} perLevel  { A1: { correct, total }, A2: {...}, ... }
 * @returns {{ technicalScore: number, estimatedCefr: string, highestPassed: string|null }}
 */
export function computeScore(perLevel) {
  // Nivel más alto donde acertó >= 3 de 5 (regla 3 del documento).
  let highestPassed = null;
  for (const level of LEVELS) {
    const r = perLevel[level];
    if (r && r.correct >= PASS_THRESHOLD) highestPassed = level;
  }

  // ── Borde (b): no superó ni siquiera A1 ────────────────────────────────────
  if (highestPassed === null) {
    const a1Correct = perLevel.A1?.correct ?? 0;
    return {
      technicalScore: round1(1.0 + a1Correct * 0.4),
      estimatedCefr: 'pre-A1',
      highestPassed: null,
    };
  }

  const correctInHighest = perLevel[highestPassed].correct;
  const nextLevel = getNextLevel(highestPassed);

  // ── Borde (a): superó C1, no hay nivel siguiente ───────────────────────────
  if (nextLevel === null) {
    const score =
      LEVEL_ANCHOR.C1 +
      (correctInHighest - 4) * WITHIN_LEVEL_WEIGHT +
      (correctInHighest / 5) * 2.4; // calibrado para que 5/5 en C1 == 10.0
    return {
      technicalScore: round1(clamp(score, 1, 10)),
      estimatedCefr: 'C1',
      highestPassed,
    };
  }

  // ── Caso general (el que reproduce ambos ejemplos del documento) ───────────
  const correctInNext = perLevel[nextLevel]?.correct ?? 0;
  const score =
    LEVEL_ANCHOR[highestPassed] +
    (correctInHighest - 4) * WITHIN_LEVEL_WEIGHT +
    correctInNext * NEXT_LEVEL_WEIGHT;

  // "A2-B1" cuando hay indicios del nivel siguiente; si no acertó nada arriba,
  // la etiqueta se queda limpia en el nivel superado.
  const estimatedCefr =
    correctInNext > 0 && perLevel[nextLevel] ? `${highestPassed}-${nextLevel}` : highestPassed;

  return {
    technicalScore: round1(clamp(score, 1, 10)),
    estimatedCefr,
    highestPassed,
  };
}

/**
 * Estado inicial del motor adaptativo.
 * @param {string} startLevel
 */
export function createCalibrationState(startLevel) {
  return {
    level: startLevel,
    slideIndex: 0,
    levelsShown: [startLevel],
    perLevel: { [startLevel]: { correct: 0, total: 0 } },
    answers: [],              // flat, en orden de aparición (Parte I §8)
    cutoffTriggeredAt: null,
    finished: false,
  };
}

/**
 * Registra una respuesta y devuelve el nuevo estado del motor.
 * Implementa el branching adaptativo completo de Parte II §1.
 *
 * @param {Object}  state    estado actual
 * @param {boolean} isCorrect
 * @returns {Object} nuevo estado (inmutable)
 */
export function registerAnswer(state, isCorrect) {
  const level = state.level;
  const prev = state.perLevel[level] ?? { correct: 0, total: 0 };

  const perLevel = {
    ...state.perLevel,
    [level]: {
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
    },
  };

  const next = {
    ...state,
    perLevel,
    answers: [...state.answers, isCorrect],
  };

  const result = perLevel[level];
  const fails = result.total - result.correct;

  // Regla 2 — corte adaptativo: falla 3 en el nivel, se detiene todo ahí.
  // El documento lo enmarca como honestidad, no como castigo: "no lo obliga a
  // sufrir preguntas muy por encima de su nivel".
  if (fails >= CUTOFF_FAILS) {
    return { ...next, cutoffTriggeredAt: level, finished: true };
  }

  const slidesInLevel = CALIBRATION_BANK[level].length;
  const isLastSlideOfLevel = result.total >= slidesInLevel;

  if (!isLastSlideOfLevel) {
    return { ...next, slideIndex: state.slideIndex + 1 };
  }

  // Nivel terminado sin corte → siempre superado (menos de 3 fallos en 5).
  const nextLevel = getNextLevel(level);
  if (nextLevel === null) {
    // Barrió C1: fin natural de la escala.
    return { ...next, finished: true };
  }

  return {
    ...next,
    level: nextLevel,
    slideIndex: 0,
    levelsShown: [...state.levelsShown, nextLevel],
    perLevel: { ...perLevel, [nextLevel]: { correct: 0, total: 0 } },
  };
}

/** Slide actual según el estado del motor. */
export function getCurrentSlide(state) {
  const levelSlides = CALIBRATION_BANK[state.level];
  if (!levelSlides) return null;
  return levelSlides[state.slideIndex] ?? null;
}

/**
 * Empaqueta el resultado final en la forma exacta del JSON de Parte II §1,
 * lista para persistir en el store y sincronizar al backend.
 */
export function buildCalibrationResult(state) {
  const { technicalScore, estimatedCefr, highestPassed } = computeScore(state.perLevel);
  return {
    levels_shown: state.levelsShown,
    per_level: state.perLevel,
    cutoff_triggered_at: state.cutoffTriggeredAt,
    technical_score: technicalScore,
    estimated_cefr: estimatedCefr,
    highest_passed: highestPassed,
    answers: state.answers,
  };
}

/* ─── helpers ─────────────────────────────────────────────────────────────── */

function round1(n) {
  return Math.round(n * 10) / 10;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}
